import { ContactWithDetails, BantQualification } from '../../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { OpportunityCard } from './OpportunityCard';
import {
  Building2,
  Calendar,
  CheckCircle2,
  Mail,
  Phone,
  RefreshCw,
  User,
  XCircle
} from 'lucide-react';

interface ContactDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactWithDetails | null;
  loading: boolean;
}

export function ContactDetailDialog({
  open,
  onOpenChange,
  contact,
  loading
}: ContactDetailDialogProps) {
  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBantScoreBadge = (score: number) => {
    if (score >= 3) {
      return <Badge variant="approved">Score: {score}/4</Badge>;
    } else if (score === 2) {
      return <Badge variant="pending">Score: {score}/4</Badge>;
    }
    return <Badge variant="rejected">Score: {score}/4</Badge>;
  };

  const getBantBadge = (value: string) => {
    if (value === 'Sim') {
      return (
        <Badge variant="approved" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Sim
        </Badge>
      );
    }
    return (
      <Badge variant="rejected" className="gap-1">
        <XCircle className="h-3 w-3" />
        Nao
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do Contato</DialogTitle>
          <DialogDescription>
            {contact?.name || contact?.phoneNumber || 'Carregando...'}
          </DialogDescription>
        </DialogHeader>

        {loading && !contact?.opportunities?.length ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : contact ? (
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Informacoes do Contato
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {contact.name && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Nome</Label>
                      <p className="text-sm font-medium">{contact.name}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label className="text-xs text-muted-foreground">Telefone</Label>
                    <p className="text-sm font-medium">{contact.phoneNumber}</p>
                  </div>
                </div>
                {contact.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <p className="text-sm font-medium">{contact.email}</p>
                    </div>
                  </div>
                )}
                {contact.company && (
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Empresa</Label>
                      <p className="text-sm font-medium">{contact.company}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Resumo
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-3 border rounded-lg text-center">
                  <Label className="text-xs text-muted-foreground">Oportunidades</Label>
                  <p className="text-2xl font-bold">{contact.totalOpportunities}</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <Label className="text-xs text-muted-foreground">Valor Total</Label>
                  <p className="text-lg font-semibold">{formatCurrency(contact.totalOpportunityValue)}</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <Label className="text-xs text-muted-foreground">Score BANT</Label>
                  <div className="mt-1">{getBantScoreBadge(contact.avgBantScore)}</div>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <Label className="text-xs text-muted-foreground">Cadastro</Label>
                  <p className="text-sm">{formatDate(contact.createdAt).split(',')[0]}</p>
                </div>
              </div>
            </div>

            {/* Opportunities Section */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Oportunidades ({contact.opportunities?.length || 0})
              </h4>
              {contact.opportunities && contact.opportunities.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {contact.opportunities.map((opportunity) => (
                    <OpportunityCard key={opportunity.id} opportunity={opportunity} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground border rounded-lg">
                  Nenhuma oportunidade encontrada
                </div>
              )}
            </div>

            {/* BANT Qualifications Section */}
            {contact.bantQualifications && contact.bantQualifications.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Historico BANT ({contact.bantQualifications.length})
                </h4>
                <div className="space-y-3">
                  {contact.bantQualifications.map((qualification: BantQualification) => (
                    <div key={qualification.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {formatDate(qualification.createdAt)}
                        </div>
                        <Badge
                          variant={qualification.qualificado === 'Sim' ? 'approved' : 'rejected'}
                        >
                          {qualification.qualificado === 'Sim' ? 'Qualificado' : 'Nao Qualificado'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Budget</div>
                          {getBantBadge(qualification.budget)}
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Authority</div>
                          {getBantBadge(qualification.authority)}
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Need</div>
                          {getBantBadge(qualification.need)}
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Timeline</div>
                          {getBantBadge(qualification.timeline)}
                        </div>
                      </div>
                      {qualification.observacoes && (
                        <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                          {qualification.observacoes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Datas
              </h4>
              <div className="grid gap-4 sm:grid-cols-2 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Criado em</Label>
                  <p>{formatDate(contact.createdAt)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Atualizado em</Label>
                  <p>{formatDate(contact.updatedAt)}</p>
                </div>
                {contact.lastOpportunityAt && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Ultima Oportunidade</Label>
                    <p>{formatDate(contact.lastOpportunityAt)}</p>
                  </div>
                )}
                {contact.lastQualificationAt && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Ultima Qualificacao</Label>
                    <p>{formatDate(contact.lastQualificationAt)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
